import { effect, inject, Injectable, untracked } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ConfirmationService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { firstValueFrom } from 'rxjs';

import { Profile } from '@/features/administration/user-profile/types/profile';
import { BrowserNavigationGuardService } from '@shared/services/browser-navigation-guard.service';
import { FileService } from '@shared/services/file.service';
import { GlossaryTooltipService } from '@shared/services/glossary-tooltip.service';
import { PersonalizationService } from '@shared/services/personalization.service';
import { SignalRService } from '@shared/services/signalr.service';
import { ToastService } from '@shared/services/ui';
import { ActiveProfileStore } from './active-profile.store';
import { AuthService } from './auth.service';
import { HOME_URL, RouteAccessService } from './route-access.service';
import { UserService } from './user.service';

/**
 * The only writer of the active profile after construction.
 *
 * Lives in `core` and reaches into `shared` only (never the reverse), so
 * `PersonalizationService` stays dependency-light enough to be read from HTTP interceptors.
 */
@Injectable({ providedIn: 'root' })
export class ActiveProfileService {
  private readonly store = inject(ActiveProfileStore);
  private readonly personalization = inject(PersonalizationService);
  private readonly userService = inject(UserService);
  private readonly authService = inject(AuthService);
  private readonly routeAccess = inject(RouteAccessService);
  private readonly router = inject(Router);
  private readonly glossary = inject(GlossaryTooltipService);
  private readonly signalr = inject(SignalRService);
  private readonly files = inject(FileService);
  private readonly browserGuard = inject(BrowserNavigationGuardService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly dialogService = inject(DialogService);
  private readonly modalService = inject(BsModalService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);

  private bootstrapInFlight = false;
  private lastSeenProfile: Profile | null | undefined = undefined;

  constructor() {
    // Route guards are NOT re-run for unchanged URL segments, so nothing else would notice
    // that the page the user is sitting on has become forbidden. `switchProfile` already
    // picks its own navigation target, so skip while it is running; this covers the other
    // way grants change - `refreshActiveProfile()` after an admin edits their own role.
    effect(() => {
      const profile = this.store.profile();
      untracked(() => {
        const isFirstRun = this.lastSeenProfile === undefined;
        const changed = this.lastSeenProfile !== profile;
        this.lastSeenProfile = profile;
        if (isFirstRun || !changed || this.store.isSwitching() || !profile) {
          return;
        }
        this.routeAccess.revalidateCurrentUrl();
      });
    });
  }

  // ------------------------------------------------------------------ switching

  /**
   * Switch the active profile with no page reload. Every step's position is load-bearing;
   * see the inline reasons.
   */
  async switchProfile(profile: Profile): Promise<boolean> {
    if (this.store.isSwitching()) {
      return false; // swallow double-clicks
    }
    if (this.store.profile()?.id === profile.id) {
      return true;
    }
    // Ask BEFORE anything mutates. Once we start, per-page canDeactivate dialogs are bypassed
    // (they could otherwise run a save under the NEW profile), so this is the user's only
    // chance to keep unsaved work.
    if (this.browserGuard.hasAnyUnsavedChanges() && !(await this.confirmDiscard())) {
      return false;
    }

    this.store.isSwitching.set(true);
    try {
      // 1. Void the previous scope first: cancels GETs already on the wire, so nothing issued
      //    from here on can be served an old-profile response.
      this.store.enter();

      // 2/3. Grants and the profile move together. `setActive` writes the signal before the
      //      RxJS bridges, so anything reacting to `profile$` already sees the new grants -
      //      and the `X-Profile` source (FundProfileInterceptor) has already flipped.
      this.apply(profile);

      // 4. Root-held state no component owns, plus body-appended overlays the router cannot
      //    tear down. The glossary re-fetch is fired, not awaited, so it is in flight during
      //    the navigation and the new page renders correct tooltips first time.
      this.closeOverlays();
      this.store.runResets();
      this.files.filesCache.set(new Map());
      this.files.filesLoading.set(new Map());
      void firstValueFrom(this.glossary.init()).catch(() => undefined);

      // 5. Re-mount the routed tree. `shouldReuseRoute = () => false` means this destroys and
      //    re-creates everything from LayoutComponent down, which is what actually erases
      //    component-local state and re-issues every query under the new header.
      //    `onSameUrlNavigation: 'reload'` is required: the default 'ignore' bails out before
      //    guards run, so a same-URL navigation would be a no-op.
      const target = this.routeAccess.isAllowed(this.routeAccess.currentRequirement())
        ? this.router.url
        : HOME_URL;
      const navigated = await this.router.navigateByUrl(target, {
        onSameUrlNavigation: 'reload',
        replaceUrl: true,
      });

      // 6. Persisted residue LAST: `base-filter.directive` re-saves list filters from
      //    ngOnDestroy, i.e. while step 5 is tearing the old page down, so clearing earlier
      //    would be silently undone.
      this.clearPersistedResidue();

      // 7. Re-authorise against the server. `/me` is X-Profile-exempt so it is safe at any
      //    point; it replaces the profile object we optimistically applied in step 2 with the
      //    authoritative one, which is what stops the persisted copy drifting.
      await this.reconcile(profile.id);

      return navigated;
    } finally {
      this.store.isSwitching.set(false);
    }
  }

  // ------------------------------------------------------------------ bootstrap

  /**
   * Single ordered bootstrap, sharing `apply()` with `switchProfile`. Idempotent, so it is
   * safe to call from both the "already signed in" path and the post-redirect path.
   */
  async bootstrap(options: { navigateWhenOnLogin: boolean }): Promise<void> {
    if (this.bootstrapInFlight || !this.authService.getActiveAccount()) {
      return;
    }
    this.bootstrapInFlight = true;
    try {
      const storedId = this.store.storedProfileId();
      const response = await firstValueFrom(this.userService.getMe());
      const profiles = response.data.profiles ?? [];

      if (!profiles.length) {
        this.personalization.clearProfile();
        this.toast.error(
          this.t('error.no.profiles.detail', 'You do not have any profiles assigned. Please contact your administrator.'),
          this.t('error.no.profiles.summary', 'No Profiles Available'),
        );
        sessionStorage.removeItem('login_in_progress');
        this.signOut();
        return;
      }

      // `>= 0`, not the previous `idx > 0 ? idx : 0`: index 0 is a legitimate match (the old
      // expression got the right answer there by luck) and, more importantly, the old code
      // also mapped "not found" (-1) to 0 with no signal to the user.
      const idx = storedId === null ? -1 : profiles.findIndex((profile) => profile.id === storedId);
      const resolved = profiles[idx >= 0 ? idx : 0];

      if (storedId !== null && idx < 0) {
        // Silently landing someone in a different role is exactly the confusion this work
        // exists to remove.
        this.toast.warning(
          this.t(
            'profile.stored_unavailable.detail',
            `Your previous profile is no longer assigned to you. Switched to ${resolved.name}.`,
            { profileName: resolved.name },
          ),
          this.t('profile.stored_unavailable.summary', 'Profile changed'),
        );
      }

      if (resolved.id !== storedId) {
        // Void anything already issued under the stale id.
        this.store.enter();
      }

      this.apply(resolved);

      sessionStorage.removeItem('login_in_progress');
      sessionStorage.setItem('loggedIn', 'true');

      this.signalr.startConnection();
      void firstValueFrom(this.glossary.init()).catch(() => undefined);

      if (options.navigateWhenOnLogin && this.isOnLoginPage()) {
        await this.router.navigateByUrl(HOME_URL);
      }
    } catch {
      sessionStorage.removeItem('login_in_progress');
      if (this.authService.getActiveAccount()) {
        this.toast.error(
          this.t('error.load.profile.detail', 'Unable to load your profile. Please try logging in again.'),
          this.t('error.load.profile.summary', 'Profile Load Failed'),
        );
        this.personalization.clearProfile();
        this.signOut();
      }
    } finally {
      this.bootstrapInFlight = false;
    }
  }

  // -------------------------------------------------------------------- helpers

  /** The one place grants and the active profile are written together. */
  private apply(profile: Profile): void {
    this.personalization.setProfile(profile);
  }

  private async reconcile(expectedProfileId: number): Promise<void> {
    try {
      const response = await firstValueFrom(this.userService.getMe());
      const profiles = response.data.profiles ?? [];
      const server = profiles.find((profile) => profile.id === expectedProfileId);
      if (server) {
        this.apply(server);
        return;
      }
      // Revoked between the click and the round-trip.
      this.toast.warning(
        this.t('profile.revoked.detail', 'That profile is no longer available.'),
        this.t('profile.revoked.summary', 'Profile changed'),
      );
      if (profiles.length) {
        this.apply(profiles[0]);
        await this.router.navigateByUrl(HOME_URL, { onSameUrlNavigation: 'reload', replaceUrl: true });
      } else {
        this.signOut();
      }
    } catch {
      // The optimistically applied grants stay in force. Every server call is still
      // authorised by `X-Profile`, so the blast radius is a possibly-too-generous menu until
      // the next `/me`.
      this.toast.error(this.t('profile.reconcile_failed.detail', 'Could not confirm your permissions.'));
    }
  }

  /**
   * Body-appended overlays live outside every router outlet, so the re-mount in step 5 does
   * not touch them: an open modal from the previous profile would keep its data and its Save
   * button.
   */
  private closeOverlays(): void {
    this.confirmationService.close();
    [...this.dialogService.dialogComponentRefMap.keys()].forEach((ref) => ref.close());
    for (let open = this.modalService.getModalsCount(); open > 0; open--) {
      this.modalService.hide();
    }
  }

  private clearPersistedResidue(): void {
    // Saved list filters hold piIds / fundTypesIds that may not exist for the new role.
    localStorage.removeItem('listQueries');
    localStorage.removeItem('dataRequestsList.activeTab');
    // The fund feature is vestigial (nothing writes it any more), but a value left by an
    // older build would silently change `allowChange()` for the new role.
    this.personalization.clearFund();
  }

  private confirmDiscard(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.confirmationService.confirm({
        header: this.t('profile.switch.unsaved.header', 'Unsaved changes'),
        message: this.t(
          'profile.switch.unsaved.message',
          'Switching profile will discard your unsaved changes. Continue?',
        ),
        acceptButtonProps: {
          severity: 'danger',
          label: this.t('profile.switch.unsaved.accept', 'Switch anyway'),
          icon: 'none',
        },
        rejectButtonProps: {
          severity: 'contrast',
          label: this.t('common.cancel', 'Cancel'),
          icon: 'none',
          text: true,
        },
        accept: () => resolve(true),
        reject: () => resolve(false),
      });
    });
  }

  private signOut(): void {
    this.authService.logout({
      onRedirectNavigate: () => {
        void this.router.navigate(['/account/login']);
      },
    });
  }

  private isOnLoginPage(): boolean {
    return window.location.pathname === '/account/login' || window.location.pathname === '/account/register';
  }

  /**
   * `TranslateService.instant` returns the KEY when a translation is missing, which is
   * truthy - so the `instant(k) || 'fallback'` pattern used elsewhere never actually falls
   * back. Compare against the key instead.
   */
  private t(key: string, fallback?: string, params?: Record<string, unknown>): string {
    const value = this.translate.instant(key, params);
    return !value || value === key ? (fallback ?? key) : value;
  }
}
